import {
    GetOperators as DLGetOperators,
    RegisterOperator as DLRegisterOperator,
    UpdateOperator as DLUpdateOperator,
    DeleteOperator as DLDeleteOperator,
    TerminateOperatorSessions as DLTerminateOperatorSessions,
    ChangePassword as DLChangePassword
} from '../../DL/Admin/Operators';

import { ValidateSqlInjection } from '../Security';

const ALLOWED_ROLES = ['SuperAdmin', 'BusinessAdmin', 'Operador', 'sound_operator', 'admin', 'master_admin'];

/**
 * Obtiene la lista de todos los operadores del sistema.
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
const GetOperators = async () => {
    try {
        const response = await DLGetOperators();
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al obtener la lista de operadores.'
        };
    }
};

/**
 * Registra un nuevo operador con validaciones de seguridad.
 * @param {Object} data - { username, password, confirmPassword, role, brandId }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const RegisterOperator = async (data) => {
    try {
        if (!data) {
            return { success: false, message: 'Los datos del operador son obligatorios.' };
        }

        const username = (data.username || '').toString().trim();
        const password = (data.password || '').toString();
        const confirmPassword = data.confirmPassword ? data.confirmPassword.toString() : null;
        const role = (data.role || '').toString().trim();
        const brandId = data.brandId ? data.brandId.toString().trim() : null;

        if (!username) {
            return { success: false, message: 'Por favor, escribe el nombre de usuario.' };
        }

        if (username.length < 3) {
            return { success: false, message: 'El nombre de usuario debe tener al menos 3 caracteres.' };
        }

        const isUsernameInjection = await ValidateSqlInjection(username);
        if (isUsernameInjection) {
            return { success: false, message: 'El nombre de usuario contiene caracteres no permitidos.' };
        }

        if (!password) {
            return { success: false, message: 'Por favor, escribe la contraseña para el nuevo operador.' };
        }

        if (password.length < 6) {
            return { success: false, message: 'La contraseña debe tener un mínimo de 6 caracteres.' };
        }

        if (confirmPassword !== null && password !== confirmPassword) {
            return { success: false, message: 'Las contraseñas ingresadas no coinciden.' };
        }

        if (!role) {
            return { success: false, message: 'Por favor, selecciona un rol para el operador.' };
        }

        const isRoleValid = ALLOWED_ROLES.some(r => r.toLowerCase() === role.toLowerCase());
        if (!isRoleValid) {
            return { success: false, message: 'El rol seleccionado no es válido.' };
        }

        const payload = {
            username,
            password,
            role,
            brandId: brandId || null
        };

        const response = await DLRegisterOperator(payload);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al registrar el operador.'
        };
    }
};

/**
 * Actualiza la configuración de un operador (rol, contraseña y/o brandId).
 * @param {string} id - ID / Guid del operador
 * @param {Object} data - { role, password, confirmPassword, brandId }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const UpdateOperator = async (id, data) => {
    try {
        if (!id || id.toString().trim() === '') {
            return { success: false, message: 'El identificador del operador es obligatorio.' };
        }

        if (!data) {
            return { success: false, message: 'No se recibieron datos para actualizar.' };
        }

        const role = data.role ? data.role.toString().trim() : null;
        const password = data.password !== undefined && data.password !== null ? data.password.toString() : '';
        const confirmPassword = data.confirmPassword !== undefined && data.confirmPassword !== null ? data.confirmPassword.toString() : null;
        const brandId = data.brandId !== undefined ? (data.brandId ? data.brandId.toString().trim() : null) : undefined;

        if (role) {
            const isRoleValid = ALLOWED_ROLES.some(r => r.toLowerCase() === role.toLowerCase());
            if (!isRoleValid) {
                return { success: false, message: 'El rol especificado no es válido.' };
            }
        }

        if (password && password.trim() !== '') {
            if (password.length < 6) {
                return { success: false, message: 'La nueva contraseña debe tener al menos 6 caracteres.' };
            }
            if (confirmPassword !== null && password !== confirmPassword) {
                return { success: false, message: 'Las nuevas contraseñas no coinciden.' };
            }
        }

        const payload = {
            role: role || undefined,
            password: password && password.trim() !== '' ? password : null,
            brandId: brandId !== undefined ? brandId : null
        };

        const response = await DLUpdateOperator(id, payload);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al actualizar el operador.'
        };
    }
};

/**
 * Elimina un operador impidiendo la autoeliminación del usuario actual.
 * @param {string} id - ID del operador a eliminar
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const DeleteOperator = async (id) => {
    try {
        if (!id || id.toString().trim() === '') {
            return { success: false, message: 'El identificador del operador es obligatorio.' };
        }

        // Validación de autoeliminación
        try {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                const currentUser = JSON.parse(rawUser);
                if (currentUser && currentUser.id && currentUser.id.toLowerCase() === id.toString().toLowerCase()) {
                    return {
                        success: false,
                        message: 'No puedes eliminar tu propio usuario administrador en sesión activa.'
                    };
                }
            }
        } catch {
            // Continuar si falla la lectura de localStorage
        }

        const response = await DLDeleteOperator(id);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al eliminar el operador.'
        };
    }
};

/**
 * Termina forzosamente todas las sesiones activas de un operador.
 * @param {string} id - ID del operador
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const TerminateOperatorSessions = async (id) => {
    try {
        if (!id || id.toString().trim() === '') {
            return { success: false, message: 'El identificador del operador es obligatorio.' };
        }

        const response = await DLTerminateOperatorSessions(id);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al forzar el cierre de sesiones.'
        };
    }
};

/**
 * Cambia la contraseña del usuario actualmente logueado.
 * @param {Object} data - { currentPassword, newPassword, confirmPassword }
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const ChangePassword = async (data) => {
    try {
        if (!data) {
            return { success: false, message: 'Los datos de cambio de contraseña son obligatorios.' };
        }

        const currentPassword = (data.currentPassword || '').toString();
        const newPassword = (data.newPassword || '').toString();
        const confirmPassword = (data.confirmPassword || '').toString();

        if (!currentPassword) {
            return { success: false, message: 'Por favor, escribe tu contraseña actual.' };
        }

        if (!newPassword) {
            return { success: false, message: 'Por favor, escribe tu nueva contraseña.' };
        }

        if (newPassword.length < 6) {
            return { success: false, message: 'La nueva contraseña debe tener al menos 6 caracteres.' };
        }

        if (confirmPassword && newPassword !== confirmPassword) {
            return { success: false, message: 'La confirmación de la nueva contraseña no coincide.' };
        }

        if (currentPassword === newPassword) {
            return { success: false, message: 'La nueva contraseña debe ser diferente a la contraseña actual.' };
        }

        const response = await DLChangePassword({
            currentPassword,
            newPassword
        });

        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Ha ocurrido un error inesperado al cambiar la contraseña.'
        };
    }
};

/**
 * Obtiene la configuración de presentación y estilo para un rol de operador.
 * @param {string} role 
 * @returns {{label: string, badgeClass: string, textClass: string, icon: string}}
 */
const GetRoleBadgeConfig = (role) => {
    if (!role) {
        return {
            label: 'Sin rol',
            badgeClass: 'bg-secondary',
            textClass: 'text-white',
            icon: 'bi bi-person'
        };
    }

    const normalized = role.toString().trim().toLowerCase();

    switch (normalized) {
        case 'superadmin':
        case 'master_admin':
            return {
                label: 'SuperAdmin',
                badgeClass: 'bg-danger text-white',
                textClass: 'text-white',
                icon: 'bi bi-shield-fill-check'
            };
        case 'businessadmin':
            return {
                label: 'BusinessAdmin',
                badgeClass: 'bg-primary text-white',
                textClass: 'text-white',
                icon: 'bi bi-briefcase-fill'
            };
        case 'operador':
        case 'admin':
            return {
                label: 'Operador',
                badgeClass: 'bg-success text-white',
                textClass: 'text-white',
                icon: 'bi bi-person-check-fill'
            };
        case 'sound_operator':
            return {
                label: 'sound_operator',
                badgeClass: 'bg-info text-dark',
                textClass: 'text-dark',
                icon: 'bi bi-music-note-beamed'
            };
        default:
            return {
                label: role,
                badgeClass: 'bg-secondary text-white',
                textClass: 'text-white',
                icon: 'bi bi-person-badge'
            };
    }
};

/**
 * Formatea una fecha ISO a formato local legible.
 * @param {string|Date} dateValue 
 * @returns {string}
 */
const FormatOperatorDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return 'N/A';
    }
};

export {
    GetOperators,
    RegisterOperator,
    UpdateOperator,
    DeleteOperator,
    TerminateOperatorSessions,
    ChangePassword,
    GetRoleBadgeConfig,
    FormatOperatorDate,
    ALLOWED_ROLES
};
